/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { formatPrice } from "@lemmonhq/orders";

interface CreditNoteFooterProps {
  /** Hidden while loading or when nothing can be credited. */
  showTotal: boolean;
  total: number;
  /** Item totals are estimates; an amount refund is exact. */
  estimated: boolean;
  currency: string;
  reopen: boolean;
  submitting: boolean;
  canSubmit: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

/**
 * Bottom bar of the credit note dialog: total on the left, actions on the
 * right. Rendered through LemmonModal's `renderButtons`, which keeps it below
 * the scrolling body on tablet and pinned at the bottom of the full-screen
 * sheet on phones (LemmonModal adds the safe-area inset there).
 */
export const CreditNoteFooter = defineComponent<CreditNoteFooterProps>({
  name: "CreditNoteFooter",
  props: [
    "showTotal",
    "total",
    "estimated",
    "currency",
    "reopen",
    "submitting",
    "canSubmit",
    "onCancel",
    "onSubmit",
  ],
  setup(props) {
    const { t } = useLocalization();
    const label = (full: string, short: string) => (
      <>
        <span class="truncate sm:hidden">{t(short)}</span>
        <span class="hidden truncate sm:inline">{t(full)}</span>
      </>
    );
    return () => (
      <div
        class="flex items-center gap-3 border-t border-base-300 bg-base-100 px-4 py-2.5 sm:px-5"
      >
        <div class="min-w-0 flex-1 leading-tight">
          {props.showTotal ? (
            <>
              <div class="text-xs text-base-content/60">
                {t("reports.tx.credit_note_items.total")}
              </div>
              <div class="font-mono text-base font-semibold text-accent">
                {props.estimated ? "≈ " : ""}
                {formatPrice(props.total, true, 2, props.currency)}
              </div>
            </>
          ) : null}
        </div>
        {/* Phones leave the full-screen sheet with its back button; tablet keeps Cancel. */}
        <button
          class="btn btn-ghost hidden shrink-0 sm:inline-flex"
          onClick={props.onCancel}
          disabled={props.submitting}
        >
          {t("general.words.cancel")}
        </button>
        <button
          class={`btn shrink-0 flex-nowrap ${props.reopen ? "btn-warning" : "btn-primary"}`}
          onClick={props.onSubmit}
          disabled={!props.canSubmit}
        >
          {props.submitting ? (
            <>
              <span class="loading loading-spinner loading-sm"></span>
              {t("reports.tx.credit_note_items.submitting")}
            </>
          ) : props.reopen ? (
            <>
              <span class="material-symbols-outlined text-xl">
                table_restaurant
              </span>
              {label(
                "reports.tx.credit_note_items.submit_reopen",
                "reports.tx.credit_note_items.submit_reopen_short",
              )}
            </>
          ) : (
            <>
              <span class="material-symbols-outlined text-xl">receipt_long</span>
              {label(
                "reports.tx.credit_note_items.submit",
                "reports.tx.credit_note_items.submit_short",
              )}
            </>
          )}
        </button>
      </div>
    );
  },
});
